import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { billingApi } from '../api/client'
import { formatMoney, catalogPrice } from '../lib/pricing'
import { useDisplayCurrency } from '../store/currencyStore'
import { useCartStore } from '../store/cartStore'
import CurrencyPicker from '../components/CurrencyPicker'
import StorefrontNav from '../components/StorefrontNav'
import StorefrontFooter from '../components/StorefrontFooter'
import PaymentLogos from '../components/PaymentLogos'
import { CycleToggle, PRODUCT_FEATURE_KEYS } from './ProductsPage'

// Detail page for one catalogue product: full description, characteristics,
// price for the selected cycle, and the quantity + add-to-cart step.
export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const currency = useDisplayCurrency()
  const cycle = useCartStore((s) => s.cycle)
  const setCycle = useCartStore((s) => s.setCycle)
  const add = useCartStore((s) => s.add)
  const [qty, setQty] = useState(1)
  const [added, setAdded] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['catalog'],
    queryFn: billingApi.getCatalog,
  })

  const product = data?.products.find((p) => p.slug === slug)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fbfbfa] flex flex-col">
        <StorefrontNav />
        <div className="flex-1 max-w-4xl w-full mx-auto px-6 py-16 text-sm text-gray-500">
          {t('common.loading')}
        </div>
        <StorefrontFooter />
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#fbfbfa] flex flex-col">
        <StorefrontNav />
        <div className="flex-1 max-w-4xl w-full mx-auto px-6 py-16">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">{t('products.notFoundTitle')}</h1>
          <Link to="/urunler" className="text-sm text-brand-600 hover:text-brand-700">
            {t('products.backToList')}
          </Link>
        </div>
        <StorefrontFooter />
      </div>
    )
  }

  const unit = catalogPrice(product, cycle, currency)
  const total = unit * qty
  const max = product.max_quantity

  const handleAdd = () => {
    add(product.sku, qty, max)
    setAdded(true)
    window.setTimeout(() => setAdded(false), 1800)
  }

  return (
    <div className="relative min-h-screen bg-[#fbfbfa] flex flex-col">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(90%_120%_at_85%_-10%,rgba(2,132,199,0.08),transparent_55%)]" />
      <StorefrontNav />

      <div className="relative flex-1 max-w-5xl w-full mx-auto px-6 py-12">
        <nav className="text-sm text-gray-400 mb-6">
          <Link to="/urunler" className="hover:text-gray-600">{t('products.title')}</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-600">{t(`products.${product.sku}.name`)}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-3">
              {t(`products.${product.sku}.name`)}
            </h1>
            <p className="text-lg text-gray-500 mb-6">{t(`products.${product.sku}.tagline`)}</p>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">{t('products.descriptionHeading')}</h2>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {t(`products.${product.sku}.description`)}
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">{t('products.featuresHeading')}</h2>
              <ul className="space-y-2.5">
                {(PRODUCT_FEATURE_KEYS[product.sku] ?? []).map((k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t(`products.${product.sku}.${k}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Buy box */}
          <div className="bg-white rounded-2xl border-2 border-brand-500 shadow-lg shadow-brand-100 p-6 lg:sticky lg:top-24">
            <div className="flex flex-col gap-3 mb-5">
              <CurrencyPicker />
              <CycleToggle cycle={cycle} onChange={setCycle} />
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">{formatMoney(unit, currency, i18n.language)}</span>
                <span className="text-gray-400 text-sm">
                  /{cycle === 'yearly' ? t('pricing.yr') : t('pricing.mo')}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('products.vatNote', { rate: product.vat_rate })}</p>
            </div>

            {max > 1 && (
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-gray-600 font-medium">{t('products.quantity')}</label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-100 font-bold"
                      aria-label={t('products.decrease')}
                    >
                      -
                    </button>
                    <span className="w-8 text-center font-semibold text-gray-900">{qty}</span>
                    <button
                      onClick={() => setQty((q) => Math.min(max, q + 1))}
                      className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-100 font-bold"
                      aria-label={t('products.increase')}
                    >
                      +
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  {t('products.lineTotal', { total: formatMoney(total, currency, i18n.language) })}
                </p>
              </div>
            )}

            {product.needs_instance && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                {t('products.needsInstanceNote')}
              </p>
            )}

            <button
              onClick={handleAdd}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
            >
              {added ? t('products.added') : t('products.addToCart')}
            </button>
            <Link
              to="/sepet"
              className="mt-2 block text-center w-full py-3 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {t('products.goToCart')}
            </Link>

            <div className="mt-5 pt-5 border-t border-gray-100">
              <PaymentLogos withCaption />
            </div>
          </div>
        </div>
      </div>

      <StorefrontFooter />
    </div>
  )
}
